import { ApiErrorResponse } from "../../types/ApiErrorResponse";

export const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return "-";

    let d: Date;

    if (typeof date === "string") {
        // Intentar parsear como ISO string primero (formato est�ndar)
        d = new Date(date);

        // Si falla, intentar parsear formatos personalizados
        if (isNaN(d.getTime())) {
            // Formato: "4/3/2026 0:00:00" o "4/3/2026"
            const parts = date.split(" ");
            const datePart = parts[0];

            const dateComponents = datePart.split("/");

            if (dateComponents.length === 3) {
                const [part1, part2, part3] = dateComponents;

                // Detectar formato por el tama�o del tercer componente
                if (part3.length === 4) {
                    // Formato M/d/yyyy (americano)
                    const month = parseInt(part1);
                    const day = parseInt(part2);
                    const year = parseInt(part3);
                    d = new Date(year, month - 1, day);
                } else {
                    // Formato dd/MM/yy
                    const day = parseInt(part1);
                    const month = parseInt(part2);
                    const year = parseInt(part3);
                    d = new Date(year, month - 1, day);
                }
            }
        }
    } else {
        d = new Date(date);
    }

    if (isNaN(d.getTime())) return "-";
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
};

export const formatDateString = (timestampInSeconds: string) => {
    const d = new Date(Number(timestampInSeconds));
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
};

export const formatTimestampToDDMMYYYY = (timestampMillis: number, locale = 'es-ES') => {
    if (timestampMillis === null || timestampMillis === undefined || typeof timestampMillis !== 'number') {
        return '';
    }
    
    if (timestampMillis <= 0) {
        return '';
    }

    try {
        const date = new Date(timestampMillis);
        
        return new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }).format(date);

    } catch (e) {
        console.error("Error al formatear la fecha:", e);
        return 'Error de formato';
    }
};

export const formatTimestampToDDMMYYYYHHMM = (timestampMillis: number, locale = 'es-ES') => {
    if (timestampMillis === null || timestampMillis === undefined || typeof timestampMillis !== 'number') {
        return '';
    }

    if (timestampMillis <= 0) {
        return '';
    }

    try {
        const date = new Date(timestampMillis);

        return new Intl.DateTimeFormat(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(date);

    } catch (e) {
        console.error("Error al formatear la fecha:", e);
        return 'Error de formato';
    }
};

export const formatToLocalTime = (utcTimestamp: string | Date): string => {
    if (!utcTimestamp) return '';
    
    try {
        const date = typeof utcTimestamp === 'string' ? new Date(utcTimestamp) : utcTimestamp;
        
        if (isNaN(date.getTime())) {
            console.error('Invalid date:', utcTimestamp);
            return '';
        }
        const userLocale = Intl.DateTimeFormat().resolvedOptions().locale;
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        return date.toLocaleString(userLocale, {
            timeZone: timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
        
    } catch (error) { 
        const fetchError = error as ApiErrorResponse;
        return (
            fetchError?.message 
        );
    }
};

export const formatDateToCompactYYYYMMDD = (value: Date | number | string = new Date()): string => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toISOString().slice(0, 10).replace(/-/g, "");
};

export const formatMonthYear = (value: Date | number | string): string => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${year}`;
};



export const formatDocumentTime = (value?: string | number | Date) => {
    if (value === null || value === undefined || value === "") return "";

    const tryFormat = (candidate: Date) => {
        if (Number.isNaN(candidate.getTime())) return "";
        return candidate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    };

    if (typeof value === "number") {
        const normalized = value < 1e12 ? value * 1000 : value;
        return tryFormat(new Date(normalized));
    }

    if (typeof value === "string") {
        const numeric = Number(value);
        if (!Number.isNaN(numeric) && value.trim() !== "") {
            const normalized = numeric < 1e12 ? numeric * 1000 : numeric;
            const formattedNumeric = tryFormat(new Date(normalized));
            if (formattedNumeric) return formattedNumeric;
        }

        return tryFormat(new Date(value));
    }

    return tryFormat(value);
};

export const formatDocumentOnlyDate = (value?: string | number | Date) => {
    if (value === null || value === undefined || value === "") return "";

    const tryFormat = (candidate: Date) => {
        if (Number.isNaN(candidate.getTime())) return "";
        return candidate.toLocaleDateString();
    };

    if (typeof value === "number") {
        const normalized = value < 1e12 ? value * 1000 : value;
        return tryFormat(new Date(normalized));
    }

    if (typeof value === "string") {
        const numeric = Number(value);
        if (!Number.isNaN(numeric) && value.trim() !== "") {
            const normalized = numeric < 1e12 ? numeric * 1000 : numeric;
            const formattedNumeric = tryFormat(new Date(normalized));
            if (formattedNumeric) return formattedNumeric;
        }

        return tryFormat(new Date(value));
    }

    return tryFormat(value);
};